import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type Branding, officeApi } from './api.js';
import { useSession } from './session.js';

interface BrandingValue {
  /** `undefined`: henüz okunmadı; `null`: ayar yok (Felixmate görünür). */
  branding: Branding | null | undefined;
  setBranding: (branding: Branding | null) => void;
}

const BrandingContext = createContext<BrandingValue | null>(null);

/**
 * Şirket markası (ADR 0019 §4) oturum açıkken bir kez okunur; Ayarlar'da kaydedilince güncellenir.
 * Okunamazsa Felixmate markası görünür; ekranlar bundan etkilenmez.
 */
export function BrandingProvider({ children }: { children: ReactNode }) {
  const { stage } = useSession();
  const [branding, setBranding] = useState<Branding | null | undefined>(undefined);

  useEffect(() => {
    if (stage !== 'active') {
      setBranding(undefined);
      return;
    }
    let live = true;
    officeApi
      .branding()
      .then((result) => live && setBranding(result.branding))
      .catch(() => live && setBranding(null));
    return () => {
      live = false;
    };
  }, [stage]);

  const set = useCallback((next: Branding | null) => setBranding(next), []);
  const value = useMemo(() => ({ branding, setBranding: set }), [branding, set]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingValue {
  const value = useContext(BrandingContext);
  if (!value) throw new Error('BrandingProvider eksik');
  return value;
}
